import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";

/**
 * MetricLineChart
 * A responsive, accessible D3 + React line chart in a dark theme with cyan/blue/purple accents.
 *
 * Props:
 *  - data: Array<{ timestamp: string | Date, value: number }>
 *  - title?: string
 *  - height?: number (default 320)
 *  - smoothness?: number (0 = straight line, 1 = smoothest)
 */
export default function MetricLineChart({
  data: raw = DEFAULT_DATA,
  title = null,
  height = 320,
  smoothness = 0,
}) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(640);

  // Handle responsive width using ResizeObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const w = Math.floor(e.contentRect.width);
        if (w > 0) setWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const data = useMemo(() => {
    return raw
      .map((d) => ({
        date: d.timestamp instanceof Date ? d.timestamp : new Date(d.timestamp),
        value: +d.value,
      }))
      .sort((a, b) => a.date - b.date);
  }, [raw]);

  const margin = { top: 28, right: 24, bottom: 36, left: 56 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);

  const [x, y, linePath, ticksX, ticksY, yFormat] = useMemo(() => {
    const dates = data.map((d) => d.date);
    const values = data.map((d) => d.value);

    const x = d3
      .scaleUtc()
      .domain(d3.extent(dates))
      .range([0, innerW]);

    const pad = (d3.max(values) ?? 1) * 0.08;
    const y = d3
      .scaleLinear()
      .domain([d3.min(values) - pad, d3.max(values) + pad])
      .nice()
      .range([innerH, 0]);

    const curveType = smoothness <= 0 ? d3.curveLinear : smoothness >= 1 ? d3.curveMonotoneX : d3.curveCatmullRom.alpha(smoothness);

    const line = d3
      .line()
      .x((d) => x(d.date))
      .y((d) => y(d.value))
      .curve(curveType);

    const ticksX = x.ticks(Math.min(8, Math.floor(innerW / 100)));
    const ticksY = y.ticks(6);
    const yFormat = d3.format(".2f");

    return [x, y, line(data), ticksX, ticksY, yFormat];
  }, [data, innerW, innerH, smoothness]);

  // Tooltip state
  const [hover, setHover] = useState(null);
  const bisect = d3.bisector((d) => d.date).center;

  function onMouseMove(e) {
    const { left } = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - left - margin.left;
    const date = x.invert(px);
    const idx = bisect(data, date);
    const d = data[Math.max(0, Math.min(data.length - 1, idx))];
    setHover(d);
  }

  function onMouseLeave() {
    setHover(null);
  }

  return (
    <div
      ref={containerRef}
      className="w-full bg-zinc-900 text-zinc-200 rounded-2xl shadow-xl p-4 border border-zinc-800"
    >
      <div className="flex items-center justify-between mb-3">
        {title && (
          <h2 className="text-lg font-semibold tracking-tight text-cyan-300 mt-0">
            {title}
          </h2>
        )}
        {data?.length ? (
          <span className="text-xs text-zinc-400">
            {d3.timeFormat("%b %d, %Y")(data[0].date)} — {" "}
            {d3.timeFormat("%b %d, %Y")(data[data.length - 1].date)}
          </span>
        ) : null}
      </div>

      <svg
        role="img"
        aria-label={`${title} line chart`}
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto select-none"
      >
        <defs>
          {/* Stroke gradient: cyan → blue → purple */}
          <linearGradient id="lineGradient" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="50%" stopColor="#60a5fa" />
            <stop offset="100%" stopColor="#a78bfa" />
          </linearGradient>
          {/* Area fade */}
          <linearGradient id="areaGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.22" />
            <stop offset="60%" stopColor="#60a5fa" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#a78bfa" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        <g transform={`translate(${margin.left},${margin.top})`}>
          {/* X grid */}
          {ticksX.map((t, i) => (
            <line
              key={`xg-${i}`}
              x1={x(t)}
              x2={x(t)}
              y1={0}
              y2={innerH}
              className="stroke-zinc-800"
              strokeDasharray="2,4"
            />
          ))}
          {/* Y grid */}
          {ticksY.map((t, i) => (
            <line
              key={`yg-${i}`}
              x1={0}
              x2={innerW}
              y1={y(t)}
              y2={y(t)}
              className="stroke-zinc-800"
              strokeDasharray="2,4"
            />
          ))}

          {/* Area under line */}
          <path
            d={d3.area()
              .x((d) => x(d.date))
              .y0(innerH)
              .y1((d) => y(d.value))
              .curve(smoothness <= 0 ? d3.curveLinear : smoothness >= 1 ? d3.curveMonotoneX : d3.curveCatmullRom.alpha(smoothness))(data)}
            fill="url(#areaGradient)"
          />

          {/* Line */}
          <path
            d={linePath || ""}
            fill="none"
            stroke="url(#lineGradient)"
            strokeWidth={2.25}
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {/* Axes */}
          <g transform={`translate(0,${innerH})`}>
            {ticksX.map((t, i) => (
              <g key={`xt-${i}`} transform={`translate(${x(t)},0)`}>
                <line y2="6" className="stroke-zinc-700" />
                <text
                  className="fill-zinc-400 text-[10px]"
                  textAnchor="middle"
                  dy="0.9em"
                >
                  {d3.timeFormat("%b %d")(t)}
                </text>
              </g>
            ))}
          </g>

          <g>
            {ticksY.map((t, i) => (
              <g key={`yt-${i}`} transform={`translate(0,${y(t)})`}>
                <line x2="-6" className="stroke-zinc-700" />
                <text
                  className="fill-zinc-400 text-[10px]"
                  textAnchor="end"
                  dx="-0.5em"
                  dy="0.32em"
                >
                  {yFormat(t)}
                </text>
              </g>
            ))}
          </g>

          <rect
            width={innerW}
            height={innerH}
            fill="transparent"
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
          />

          {hover && (
            <g transform={`translate(${x(hover.date)},${y(hover.value)})`}>
              <circle r={4} fill="#22d3ee" stroke="#0ea5b7" strokeWidth={1.5} />
              <foreignObject x={8} y={-36} width={200} height={48}>
                <div className="px-2 py-1 rounded-md bg-zinc-800/90 border border-zinc-700 shadow-lg">
                  <div className="text-[10px] leading-tight text-zinc-400">
                    {d3.timeFormat("%b %d, %Y")(hover.date)}
                  </div>
                  <div className="text-sm font-semibold text-cyan-300">
                    {yFormat(hover.value)}
                  </div>
                </div>
              </foreignObject>
            </g>
          )}
        </g>
      </svg>

      <div className="mt-3 flex items-center gap-3 text-xs">
        <span className="inline-flex items-center gap-2 text-zinc-400">
          <span className="h-2 w-6 rounded-full" style={{ background: "linear-gradient(90deg,#22d3ee,#60a5fa,#a78bfa)" }} />
          Series value
        </span>
        <span className="ml-auto text-[11px] text-zinc-500">
          Built with D3 + React • Dark theme
        </span>
      </div>
    </div>
  );
}

const DEFAULT_DATA = [
  { timestamp: "2018-04-03T00:00:00+00:00", value: 1.83 },
  { timestamp: "2018-04-04T00:00:00+00:00", value: 1.74 },
  { timestamp: "2018-04-05T00:00:00+00:00", value: 1.75 },
  { timestamp: "2018-04-06T00:00:00+00:00", value: 1.75 },
  { timestamp: "2018-04-09T00:00:00+00:00", value: 1.75 },
  { timestamp: "2018-04-10T00:00:00+00:00", value: 1.75 },
  { timestamp: "2018-04-11T00:00:00+00:00", value: 1.76 },
  { timestamp: "2018-04-12T00:00:00+00:00", value: 1.73 },
  { timestamp: "2018-04-13T00:00:00+00:00", value: 1.72 },
  { timestamp: "2018-04-16T00:00:00+00:00", value: 1.77 },
  { timestamp: "2018-04-17T00:00:00+00:00", value: 1.76 },
  { timestamp: "2018-04-18T00:00:00+00:00", value: 1.75 },
  { timestamp: "2018-04-19T00:00:00+00:00", value: 1.73 },
  { timestamp: "2018-04-20T00:00:00+00:00", value: 1.72 },
  { timestamp: "2018-04-23T00:00:00+00:00", value: 1.7 },
  { timestamp: "2018-04-24T00:00:00+00:00", value: 1.71 },
  { timestamp: "2018-04-25T00:00:00+00:00", value: 1.71 },
  { timestamp: "2018-04-26T00:00:00+00:00", value: 1.72 },
  { timestamp: "2018-04-27T00:00:00+00:00", value: 1.72 },
  { timestamp: "2018-04-30T00:00:00+00:00", value: 1.77 },
  { timestamp: "2018-05-01T00:00:00+00:00", value: 1.76 },
];
